import React, { useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import Spinner from './Spinner';
import { BORDER, SURFACE_CARD, TEXT_MUTED, TEXT_PRIMARY, btn } from './tokens';

/**
 * Modal shell.
 *
 * Eight hand-rolled modals across the module, ~1,144 lines, all repeating the
 * same overlay + panel. Six of the eight had no close button, none handled
 * ESC, and only one closed on a backdrop click — so most of them could only
 * be dismissed by completing or cancelling the form.
 *
 * This adds the escape hatches, locks background scroll (the originals let
 * the page behind scroll under the overlay), and keeps the footer pinned so
 * the actions stay reachable on a short viewport.
 */
const SIZES = {
  sm: 'max-w-md',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
};

const VoiceModal = ({
  open = true,
  onClose,
  title,
  subtitle,
  size = 'md',
  children,
  footer,
  closeOnBackdrop = true,
}) => {
  // ESC to close, and don't let the page behind scroll while open.
  useEffect(() => {
    if (!open) return undefined;

    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    const prevOverflow = document.body.style.overflow;

    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';

    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      onMouseDown={(e) => {
        // Only a click that both starts and ends on the backdrop closes —
        // otherwise dragging a text selection out of an input dismisses the
        // form and loses the user's work.
        if (closeOnBackdrop && e.target === e.currentTarget) onClose?.();
      }}
      role="dialog"
      aria-modal="true"
      aria-label={typeof title === 'string' ? title : undefined}
    >
      <div
        className={`w-full ${SIZES[size] || SIZES.md} ${SURFACE_CARD} rounded-2xl border ${BORDER} shadow-2xl flex flex-col max-h-[calc(100dvh-2rem)]`}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className={`flex items-start gap-4 px-5 py-4 border-b ${BORDER} flex-shrink-0`}>
          <div className="min-w-0 flex-1">
            <h2 className={`text-base font-semibold truncate ${TEXT_PRIMARY}`}>{title}</h2>
            {subtitle && <p className={`text-[13px] mt-0.5 ${TEXT_MUTED}`}>{subtitle}</p>}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 -m-1 rounded-lg text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-white/[0.06] transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 py-4 overflow-y-auto min-h-0 flex-1">{children}</div>

        {footer && (
          <div className={`px-5 py-4 border-t ${BORDER} flex-shrink-0`}>{footer}</div>
        )}
      </div>
    </div>
  );
};

/**
 * Standard cancel/confirm footer.
 *
 * The same 18-line button pair was pasted into seven of the eight modals.
 */
export const ModalFooter = ({
  onCancel,
  onConfirm,
  confirmLabel = 'Save',
  cancelLabel = 'Cancel',
  loading = false,
  disabled = false,
  variant = 'primary',
}) => (
  <div className="flex gap-3 justify-end">
    <button type="button" onClick={onCancel} className={btn('secondary')} disabled={loading}>
      {cancelLabel}
    </button>
    <button
      type="button"
      onClick={onConfirm}
      disabled={loading || disabled}
      className={btn(variant)}
    >
      {loading && <Spinner size="xs" inherit />}
      {loading ? 'Saving…' : confirmLabel}
    </button>
  </div>
);

export default VoiceModal;
